<script lang="ts">
import type { PinInputRootProps, PinInputRootEmits } from 'reka-ui';
import type { Colors, Variants, Sizes } from 'types';

type InputPinType = 'text' | 'number';

interface InputPinProps<T extends InputPinType = 'text'> extends Pick<
  PinInputRootProps<T>,
  | 'defaultValue'
  | 'disabled'
  | 'id'
  | 'mask'
  // | 'modelValue'
  | 'name'
  | 'otp'
  | 'placeholder'
  | 'required'
  | 'type'
> {
  /**
   * The element or component this component should render as.
   * @defaultValue 'div'
   */
  as?: any;
  color?: Colors;
  variant?: Exclude<Variants, 'ghost' | 'link'>;
  size?: Sizes;
  /**
   * The number of input fields.
   * @defaultValue 6
   */
  length?: number | string;
  autofocus?: boolean;
  autofocusDelay?: number;
  class?: string;
}

type InputPinValue<T extends InputPinType = 'text'> = Pick<PinInputRootProps<T>, 'modelValue'>;
export type InputPinEmits<T extends InputPinType = 'text'> = PinInputRootEmits<T> & {
  change: [value: InputPinValue];
  blur: [value: InputPinValue];
};
</script>

<script setup lang="ts" generic="T extends InputPinType = 'text'">
import { PinInputInput, PinInputRoot, useForwardPropsEmits } from 'reka-ui';
import { ref, onMounted, computed, toRef, watch } from 'vue';
import type { ComponentPublicInstance } from 'vue';
import { reactivePick } from '@vueuse/core';
import { useFormAssociated } from 'composables/useFormAssociated';

// Add a local model so the component can manage its own value (needed for CE + forms)
const [modelValue] = defineModel<string[]>();

const props = withDefaults(defineProps<InputPinProps>(), {
  id: 'otp',
  placeholder: '○',
  color: 'neutral',
  variant: 'soft',
  size: 'md',
  length: 6
});

const emit = defineEmits<(e: 'change' | 'blur', value: InputPinValue<T>) => void>();

// Do NOT forward modelValue as a plain prop; bind it via v-model instead
const rootProps = useForwardPropsEmits(
  reactivePick(
    props,
    'defaultValue',
    'disabled',
    'id',
    'mask',
    // 'modelValue',  // removed
    'name',
    'otp',
    'placeholder',
    'required',
    'type'
  ),
  emit
);

const inputsRef = ref<ComponentPublicInstance[]>([]);

// First underlying input element acts as the form proxy
const inputRef = ref<HTMLInputElement | null>(null);

// Map model to a single string for form posting
const modelRef = modelValue; // watch/validate against the local model

// Expose props as refs so linter/template see them
const required = toRef(props, 'required');
const disabled = toRef(props, 'disabled');

const { validate } = useFormAssociated<unknown>({
  inputRef,
  modelValue: modelRef,
  disabled, // use refs instead of props.<x>
  required,
  toFormValue: v => {
    if (Array.isArray(v)) {
      const s = v.join('');
      return s === '' ? null : s;
    }
    if (typeof v === 'string') return v === '' ? null : v;
    return v == null ? null : String(v);
  }
});

function combinedValue(): string {
  const v = modelRef.value as any;
  if (Array.isArray(v)) return v.join('');
  return typeof v === 'string' ? v : '';
}

// Ensure validity reflects “complete code” when required
function updateCustomValidity() {
  const el = inputRef.value;
  const len = Number(props.length) || 0;
  const v = combinedValue();
  const isRequired = !!required.value; // use ref

  if (el) {
    el.value = v;
    el.minLength = len || 0;
  }

  const isComplete = len > 0 ? v.length >= len : v.length > 0;
  if (isRequired && !isComplete) el?.setCustomValidity('Please enter the full code');
  else el?.setCustomValidity('');
  validate();
}

function onComplete(value: string[]) {
  emit('change', value as InputPinValue<T>);
  updateCustomValidity();
}

function onInputBlur() {
  emit('blur', modelRef.value as InputPinValue<T>);
  updateCustomValidity();
}

function autoFocus() {
  if (props.autofocus) {
    inputsRef.value[0]?.$el?.focus();
  }
}

onMounted(() => {
  setTimeout(() => {
    autoFocus();
  }, props.autofocusDelay);

  // Initial validity
  updateCustomValidity();
});

// Re-validate when value or constraints change
watch(modelRef, () => updateCustomValidity());
watch(required, () => updateCustomValidity()); // use ref
watch(
  () => props.length,
  () => updateCustomValidity()
);

const classes = computed(() => [
  'e-input-pin',
  props.color && `e-input-pin-color-${props.color}`,
  props.variant && `e-input-pin-variant-${props.variant}`,
  props.size && `e-input-pin-size-${props.size}`,
  props.class
]);

defineExpose({
  inputsRef
});
</script>

<template>
  <pin-input-root
    v-model="modelValue"
    v-bind="{ ...rootProps }"
    :class="classes"
    @complete="onComplete"
  >
    <pin-input-input
      v-for="index in Number(props.length)"
      :key="index - 1"
      :ref="el => (inputsRef[index - 1] = el as ComponentPublicInstance)"
      :disabled="disabled"
      :required="required"
      :index="index - 1"
      class="e-input-pin-input"
      @blur="onInputBlur"
    />
  </pin-input-root>

  <!-- Hidden proxy input used for validity + ElementInternals form value -->
  <input
    ref="inputRef"
    type="text"
    :required="required"
    :disabled="disabled"
    :value="combinedValue()"
    :minlength="Number(props.length) || undefined"
    aria-hidden="true"
    tabindex="-1"
    class="e-input-pin-proxy"
    style="position: absolute; left: -9999px; width: 0; height: 0; opacity: 0; pointer-events: none"
  />
</template>
